import { useState, useEffect, useRef } from "react";

interface SlovakCity {
    name: string;
    zip: string;
    district: string;
}

let _citiesCache: SlovakCity[] | null = null;
let _loadPromise: Promise<SlovakCity[]> | null = null;

function loadCities(): Promise<SlovakCity[]> {
    if (_citiesCache) return Promise.resolve(_citiesCache);
    if (_loadPromise) return _loadPromise;
    _loadPromise = fetch('/slovakia.json')
        .then(r => r.json())
        .then((data: SlovakCity[]) => {
            _citiesCache = data;
            return data;
        });
    return _loadPromise;
}

const strip = (s: string) => s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
const KOSICE_KEY = strip('Košice');

interface CityAutocompleteProps {
    value: string;
    onChange: (city: string) => void;
    onCitySelect: (city: string, psc: string, district: string) => void;
    disabled?: boolean;
    required?: boolean;
    readOnly?: boolean;
}

const CityAutocomplete = ({
    value,
    onChange,
    onCitySelect,
    disabled,
    required,
    readOnly,
}: CityAutocompleteProps) => {
    const [cities, setCities] = useState<SlovakCity[]>(_citiesCache || []);
    const [suggestions, setSuggestions] = useState<SlovakCity[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!_citiesCache) {
            loadCities().then(data => setCities(data));
        }
    }, []);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const q = e.target.value;
        onChange(q);

        if (q.length < 2 || cities.length === 0) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }

        const lower = strip(q);
        const filtered = cities
            .filter(c => strip(c.name).includes(lower))
            .sort((a, b) => {
                const aKosice = strip(a.district).includes(KOSICE_KEY) ? 0 : 1;
                const bKosice = strip(b.district).includes(KOSICE_KEY) ? 0 : 1;
                if (aKosice !== bKosice) return aKosice - bKosice;
                // within same district tier, startsWith before contains
                const aStarts = strip(a.name).startsWith(lower) ? 0 : 1;
                const bStarts = strip(b.name).startsWith(lower) ? 0 : 1;
                return aStarts - bStarts;
            })
            .slice(0, 10);
        setSuggestions(filtered);
        setShowDropdown(filtered.length > 0);
        setActiveIndex(-1);
    };

    const handleSelect = (city: SlovakCity) => {
        onCitySelect(city.name, city.zip, city.district);
        setSuggestions([]);
        setShowDropdown(false);
        setActiveIndex(-1);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!showDropdown) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && activeIndex >= 0) {
            e.preventDefault();
            handleSelect(suggestions[activeIndex]);
        } else if (e.key === 'Escape') {
            setShowDropdown(false);
        }
    };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={containerRef} className="relative">
            <input
                type="text"
                value={value}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                    if (value.length >= 2 && suggestions.length > 0) setShowDropdown(true);
                }}
                className="form-input"
                placeholder="Začnite písať mesto..."
                disabled={disabled}
                readOnly={readOnly}
                required={required}
                autoComplete="off"
            />
            {showDropdown && (
                <ul className="absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-60 overflow-auto">
                    {suggestions.map((city, i) => (
                        <li
                            key={`${city.name}-${city.zip}-${i}`}
                            className={`px-3 py-2 cursor-pointer ${i === activeIndex ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                            onMouseDown={() => handleSelect(city)}
                        >
                            <div className="font-medium text-sm">{city.name}</div>
                            <div className="text-xs text-gray-500">{city.district}</div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default CityAutocomplete;
